import React, { useEffect, useRef, useCallback, useState } from 'react'
import { FiCheckCircle, FiXCircle } from 'react-icons/fi'
import NumberFormat, { NumberFormatProps } from 'react-number-format'
import { Spinner } from 'reactstrap'

import { useField } from '@unform/core'

import * as S from '../styles'

export type IInputMaskProps = Omit<NumberFormatProps, 'name'> & InputMaskProps

const InputMask: React.FC<IInputMaskProps> = ({
  name,
  icon: Icon,
  format,
  mask,
  onBlurCustom,
  loading = false,
  showBadgeIsValid = false,
  showBadgeIsInvalid = false,
  disabled = false,
  showWarningOnRequired = true,
  iconRight,
  onBlur,
  ...rest
}) => {
  const inputRef =
    useRef<NumberFormat<React.InputHTMLAttributes<HTMLInputElement>>>(null)

  const [isFilled, setIsFilled] = useState(false)

  const { fieldName, defaultValue, error, registerField, clearError } =
    useField(name)

  const handleInputBlur = useCallback(
    (e: React.FocusEvent<HTMLInputElement>) => {
      if (onBlurCustom && inputRef.current) {
        onBlurCustom({
          value: inputRef.current.state?.value || '',
          name: inputRef.current.state?.name || fieldName,
          clearError,
          setIsFilled
        })
      }

      // Call standard NumberFormat onBlur if provided
      if (onBlur) {
        onBlur(e)
      }

      setIsFilled(!!inputRef.current?.state?.value)
    },
    [clearError, onBlurCustom, onBlur, fieldName]
  )

  useEffect(() => {
    if (inputRef.current) {
      registerField({
        name: fieldName,
        ref: inputRef.current,
        path: 'state.value'
      })
    }
  }, [fieldName, registerField])

  /**
   * Handle initial value
   */
  const defaultValueIsNull = defaultValue === null
  const defaultValueIsUndefined = typeof defaultValue === 'undefined'

  useEffect(() => {
    if (!defaultValueIsNull && !defaultValueIsUndefined) {
      setIsFilled(true)
    }
  }, [defaultValue, defaultValueIsNull, defaultValueIsUndefined])

  return (
    <>
      <S.Container
        isErrored={!!error || showBadgeIsInvalid}
        isDisabled={disabled}
        showWarning={!isFilled && showWarningOnRequired && rest?.required}
      >
        {Icon && (typeof Icon === 'function' ? <Icon /> : Icon)}

        <NumberFormat
          onBlur={handleInputBlur}
          name={fieldName}
          aria-label={fieldName}
          mask={mask || '_'}
          defaultValue={defaultValue === null ? '' : defaultValue}
          ref={inputRef}
          disabled={disabled}
          {...(format ? { format } : {})}
          {...rest}
        />

        {showBadgeIsValid && (
          <S.IconWrapper className="success rightElement">
            <FiCheckCircle />
          </S.IconWrapper>
        )}

        {showBadgeIsInvalid && (
          <S.IconWrapper className="error rightElement">
            <FiXCircle />
          </S.IconWrapper>
        )}

        {loading && (
          <Spinner
            className="rightElement"
            type="grow"
            size="sm"
            color="secondary"
          />
        )}

        {iconRight && (
          <div className="right-icon rightElement">
            {typeof iconRight === 'function'
              ? React.createElement(iconRight)
              : iconRight}
          </div>
        )}
      </S.Container>
      {error && <S.MessageError>{error}</S.MessageError>}
    </>
  )
}

export default InputMask
